import React from "react";

type GuestInfoCardProps = {
  data: any;
};

const GuestInfoCard: React.FC<GuestInfoCardProps> = ({ data }) => {
  const rooms = Array.isArray(data?.rooms) ? data.rooms : [];

  // Fallbacks
  const roomTypeFallback = data?.room_type || "N/A";
  const boardTypeFallback =
    (data?.room_rate_description && data.room_rate_description.trim()) ||
    "Room Only";

  // Confirmation numbers / itinerary
  const confirmation =
    (Array.isArray(data?.response?.confirmation_numbers) &&
      data.response.confirmation_numbers.length > 0 &&
      data.response.confirmation_numbers.join(" | ")) ||
    data?.response?.itinerary_id ||
    data?.transaction_ref_no ||
    "N/A";

  // Status mapping
  const statusCode = data?.status || data?.response?.reservation_status_code;
  const status =
    (statusCode === "CF" && "Confirmed") ||
    (statusCode === "CX" && "Cancelled") ||
    (statusCode === "RQ" && "On Request") ||
    (typeof statusCode === "string" ? statusCode : "N/A");

  // Currency + Rate (use total; if multiple rooms, show per-room estimate)
  const currency = data?.currency_code || "INR";
  const totalRateNum = Number(data?.chargeable_rate ?? 0);
  const roomCount = rooms.length || 1;
  const perRoomRate = totalRateNum > 0 ? totalRateNum / roomCount : 0;

  const fmt = (n: number) =>
    isFinite(n) ? `${currency} ${n.toFixed(2)}` : `${currency} 0.00`;

  // Guest name display helper (reservation_info preferred; fall back to first adult)
  const reservationFirst =
    data?.reservation_info?.first_name ||
    rooms?.[0]?.adult_details?.[0]?.first_name;
  const reservationLast =
    data?.reservation_info?.last_name ||
    rooms?.[0]?.adult_details?.[0]?.last_name;
  const reservationTitle =
    data?.reservation_info?.title || rooms?.[0]?.adult_details?.[0]?.prefix;
  const primaryGuestName =
    [reservationTitle, reservationFirst, reservationLast]
      .filter(Boolean)
      .join(" ")
      .trim() || "Guest";

  return (
    <div className="card my-3">
      <div className="card-header card-head-bg">Guest Information</div>

      <div className="card-body">
        <table className="table text-end">
          <thead>
            <tr>
              <th className="text-center min-w-[100px] !text-gray-700">
                ROOMS
              </th>
              <th className="text-center min-w-[100px] !text-gray-700">
                GUEST
              </th>
              <th className="text-center min-w-[100px]">ADULTS</th>
              <th className="text-center min-w-[100px] !text-gray-700">
                CHILD
              </th>
              <th className="text-center min-w-[100px]">ROOM TYPE</th>
              <th className="text-center min-w-[100px]">BOARD TYPE</th>
              <th className="text-center min-w-[140px]">CONFIRMATION NO.</th>
              <th className="text-center min-w-[100px]">STATUS</th>
              <th className="text-center min-w-[120px]">RATES</th>
            </tr>
          </thead>
          <tbody>
            {(rooms.length ? rooms : [null]).map((room: any, idx: number) => {
              const adults = room?.adult_details?.length ?? 0;
              const children = room?.child_details?.length ?? 0;

              const roomType = room?.room_type || roomTypeFallback;
              const boardType =
                (room?.board_type && String(room.board_type).trim()) ||
                boardTypeFallback;

              // Per-row guest name (fallback to reservation guest if no per-room adult present)
              const rowFirst =
                room?.adult_details?.[0]?.first_name || reservationFirst;
              const rowLast =
                room?.adult_details?.[0]?.last_name || reservationLast;
              const rowPrefix =
                room?.adult_details?.[0]?.prefix || reservationTitle;
              const guestName =
                [rowPrefix, rowFirst, rowLast]
                  .filter(Boolean)
                  .join(" ")
                  .trim() || primaryGuestName;

              // Rate: show per-room estimate if multiple rooms
              const displayRate = fmt(perRoomRate || totalRateNum);

              return (
                <tr key={idx}>
                  <td className="text-center">
                    <div className="sort">{`Room ${idx + 1}`}</div>
                  </td>
                  <td className="text-center">
                    <div className="sort">{guestName}</div>
                  </td>
                  <td className="text-center">
                    <div className="sort">{adults}</div>
                  </td>
                  <td className="text-center">
                    <div className="sort">{children}</div>
                  </td>
                  <td className="text-center">
                    <div className="sort">{roomType}</div>
                  </td>
                  <td className="text-center">
                    <div className="sort">{boardType}</div>
                  </td>
                  <td className="text-center">
                    <div className="sort">{confirmation}</div>
                  </td>
                  <td className="text-center fw-bold">
                    <div className="sort text-success">{status}</div>
                  </td>
                  <td className="text-center">
                    <div className="sort">{displayRate}</div>
                    <small className="text-muted fs-12">
                      ({Number(data?.tax ?? 0) > 0 ? "Tax Incl" : "Tax Incl"})
                    </small>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Optional: total row (visible when multiple rooms) */}
        {rooms.length > 1 && (
          <div className="d-flex justify-content-end mt-2">
            <div className="fw-semibold">
              Total: <span className="fw-bold">{fmt(totalRateNum)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestInfoCard;
